
import { Box } from '@mui/material';
import { WarningSharp } from '@mui/icons-material';
import { DateFormats, TimeFormats } from '@uplift/constants';
import { useTimezone } from '@uplift/contexts';
import { useL10n } from '@uplift/hooks';
import { CalendarDateService } from '@uplift/services';
import { AppointmentType } from '@uplift/api';
import { Button, TableColumnRenderCell } from '@uplift/ui-v2';
import { useUnsubmittedSessionsContent } from '../../context';
import { UnsubmittedSessionRow } from '../../types';
import { ContentStack } from './content-stack.component';
import { useStyles } from './styles';

type UnsubmittedTableColumnRenderArgs<Field extends keyof UnsubmittedSessionRow> = TableColumnRenderCell<UnsubmittedSessionRow, Field>;

export const DateTimeCell: React.VFC<UnsubmittedTableColumnRenderArgs<'fromDatetime'>> = ({ value }) => {
  const timezone = useTimezone();

  if (typeof value === 'string') {
    const date = CalendarDateService.date(value).tz(timezone);

    return (
      <ContentStack
        primary={date.format(DateFormats.DEFAULT_DATE)}
        secondary={date.format(TimeFormats['h:mm A'])}
      />
    );
  }

  return null;
};

export type DetailsCellProps = {
  appointmentType: AppointmentType;
};

export const DetailsCell: React.VFC<DetailsCellProps> = ({ appointmentType }) => {
  const l10n = useL10n();
  const type = appointmentType === AppointmentType.psychiatry ? l10n.appointments.type.psychiatricCare : l10n.appointments.type.therapy;
  const duration = l10n.appointments.duration.sixty;

  return (
    <ContentStack
      primary={type}
      secondary={duration}
    />
  );
};

export const ActionCell: React.FC<UnsubmittedTableColumnRenderArgs<'appointmentId'>> = ({ value: appointmentId, row: { appointmentState } }) => {
  const { classes } = useStyles();
  const { onSubmitSession, onToggleCancelSessionModal } = useUnsubmittedSessionsContent();
  const isExpired = appointmentState === 'expired';

  return (
    <Box className={classes.cellRoot}>
      {isExpired && (
        <Box display="flex" mr={1}>
          <WarningSharp className={classes.icon} />
        </Box>
      )}
      <Box display="flex" alignItems="center" gap="1rem">
        <Button
          variant="contained"
          onClick={() => onSubmitSession(appointmentId)}
          disabled={isExpired}
          color="secondary"
        >
          Submit
        </Button>
        <Button
          variant="outlined"
          onClick={() => onToggleCancelSessionModal(appointmentId)}
        >
          Cancel
        </Button>
      </Box>
    </Box>
  );
};
